import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check } from 'lucide-react';
import { useSignupUrl } from '@/hooks/useSignupUrl';
import { useLandingSectionTracking } from '@/hooks/useLandingSectionTracking';
import { trackLandingCtaClicked } from '@/lib/analytics';
import { useTranslation } from '@/i18n/i18n';

const tiers = [
  {
    index: 1,
    name: 'biura.pricing.tier1.name',
    price: 'biura.pricing.tier1.price',
    description: 'biura.pricing.tier1.description',
    features: [
      'biura.pricing.tier1.feature1',
      'biura.pricing.tier1.feature2',
      'biura.pricing.tier1.feature3',
    ],
  },
  {
    index: 2,
    name: 'biura.pricing.tier2.name',
    price: 'biura.pricing.tier2.price',
    description: 'biura.pricing.tier2.description',
    features: [
      'biura.pricing.tier2.feature1',
      'biura.pricing.tier2.feature2',
      'biura.pricing.tier2.feature3',
    ],
  },
  {
    index: 3,
    name: 'biura.pricing.tier3.name',
    price: 'biura.pricing.tier3.price',
    description: 'biura.pricing.tier3.description',
    features: [
      'biura.pricing.tier3.feature1',
      'biura.pricing.tier3.feature2',
      'biura.pricing.tier3.feature3',
    ],
  },
] as const;

export function BiuraPricing() {
  const sectionRef = useLandingSectionTracking('biura_pricing');
  const signupUrl = useSignupUrl({ entityType: 'biuro', source: 'biura_pricing' });
  const { t } = useTranslation();

  return (
    <section
      ref={sectionRef}
      id="biura-pricing"
      className="px-6 md:px-8 py-16 bg-background"
      aria-labelledby="biura-pricing-heading"
    >
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl mb-10">
          <h2
            id="biura-pricing-heading"
            className="text-3xl md:text-4xl font-semibold tracking-tight"
          >
            {t('biura.pricing.heading')}
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">{t('biura.pricing.subheading')}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {tiers.map((tier) => {
            const priceLabel = t(tier.price);
            const hasMonthlyPrice = /^\d/.test(priceLabel);

            return (
              <Card
                key={tier.name}
                className="p-6 h-full rounded-2xl shadow-sm data-[featured=true]:border-primary"
                data-featured={tier.index === 1}
              >
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">
                  {t(tier.name)}
                </p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-mono tabular-nums text-4xl font-bold">{priceLabel}</span>
                  {hasMonthlyPrice && (
                    <span className="font-mono tabular-nums text-sm text-muted-foreground">
                      {t('biura.pricing.perMonth')}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-6">{t(tier.description)}</p>
                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex gap-2 text-sm">
                      <Check
                        className="size-4 text-success mt-0.5 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <span>{t(feature)}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full rounded-full"
                  variant={tier.index === 1 ? 'default' : 'outline'}
                  asChild
                >
                  <a
                    href={signupUrl}
                    onClick={() =>
                      trackLandingCtaClicked(
                        `biura_pricing_tier_${tier.index}`,
                        t('biura.pricing.cta'),
                        {
                          entity_type: 'biuro',
                        }
                      )
                    }
                  >
                    {t('biura.pricing.cta')}
                  </a>
                </Button>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
